/**
 * SIB (Sound in Background) Store
 * Manages persistent background audio playback across the entire app.
 * Audio plays independently of video/script playback — it is always a passenger, never the master clock,
 * unless the user is in Audio-Led script creation mode.
 */

import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { AudioChanPost } from "./audiochan-types"

export interface SibTrack {
  id: string
  title: string
  artist: string
  artistAvatar?: string
  audioUrl: string
  duration: number
  tags: string[]
  audienceType: string[]
  postUrl: string
}

export type SibRepeatMode = "none" | "one" | "all"

interface SibState {
  // Playback state
  isPlaying: boolean
  currentTrack: SibTrack | null
  currentTime: number
  volume: number
  isMuted: boolean
  repeatMode: SibRepeatMode
  isMinimized: boolean
  isVisible: boolean

  // Queue
  queue: SibTrack[]
  queueIndex: number

  // Audio-led mode (for Script Creator integration)
  isAudioLedMode: boolean
  audioLedLoopStart: number | null // seconds
  audioLedLoopEnd: number | null   // seconds

  // Upload stub (disabled, reserved for future use)
  uploadEnabled: boolean

  // Actions
  play: (track: SibTrack) => void
  pause: () => void
  resume: () => void
  stop: () => void
  next: () => void
  prev: () => void
  setVolume: (volume: number) => void
  setMuted: (muted: boolean) => void
  setCurrentTime: (time: number) => void
  setRepeatMode: (mode: SibRepeatMode) => void
  setMinimized: (minimized: boolean) => void
  setVisible: (visible: boolean) => void

  // Queue management
  addToQueue: (track: SibTrack) => void
  removeFromQueue: (index: number) => void
  clearQueue: () => void
  playFromQueue: (index: number) => void

  // Audio-led mode
  setAudioLedMode: (enabled: boolean) => void
  setAudioLedLoop: (start: number | null, end: number | null) => void
}

function audioChanPostToTrack(post: AudioChanPost): SibTrack {
  return {
    id: post.id,
    title: post.title,
    artist: post.artist,
    artistAvatar: post.artistAvatar,
    audioUrl: post.audioUrl,
    duration: post.duration,
    tags: post.tags,
    audienceType: post.audienceType,
    postUrl: post.postUrl,
  }
}

export { audioChanPostToTrack }

export const useSibStore = create<SibState>()(
  persist(
    (set, get) => ({
      isPlaying: false,
      currentTrack: null,
      currentTime: 0,
      volume: 0.7,
      isMuted: false,
      repeatMode: "none",
      isMinimized: false,
      isVisible: false,
      queue: [],
      queueIndex: -1,
      isAudioLedMode: false,
      audioLedLoopStart: null,
      audioLedLoopEnd: null,
      uploadEnabled: false, // Reserved for future use

      play: (track) => {
        set({
          currentTrack: track,
          isPlaying: true,
          isVisible: true,
          currentTime: 0,
        })
      },

      pause: () => set({ isPlaying: false }),

      resume: () => {
        const { currentTrack } = get()
        if (currentTrack) set({ isPlaying: true })
      },

      stop: () => set({ isPlaying: false, currentTime: 0 }),

      next: () => {
        const { queue, queueIndex, repeatMode } = get()
        if (queue.length === 0) return

        let nextIndex = queueIndex + 1
        if (nextIndex >= queue.length) {
          if (repeatMode === "all") nextIndex = 0
          else return
        }

        set({
          currentTrack: queue[nextIndex],
          queueIndex: nextIndex,
          isPlaying: true,
          currentTime: 0,
        })
      },

      prev: () => {
        const { queue, queueIndex, currentTime } = get()
        // If more than 3 seconds in, restart current track
        if (currentTime > 3) {
          set({ currentTime: 0 })
          return
        }
        if (queue.length === 0 || queueIndex <= 0) return
        const prevIndex = queueIndex - 1
        set({
          currentTrack: queue[prevIndex],
          queueIndex: prevIndex,
          isPlaying: true,
          currentTime: 0,
        })
      },

      setVolume: (volume) => set({ volume: Math.max(0, Math.min(1, volume)) }),
      setMuted: (muted) => set({ isMuted: muted }),
      setCurrentTime: (time) => set({ currentTime: time }),
      setRepeatMode: (mode) => set({ repeatMode: mode }),
      setMinimized: (minimized) => set({ isMinimized: minimized }),
      setVisible: (visible) => set({ isVisible: visible }),

      addToQueue: (track) => {
        const { queue, currentTrack } = get()
        const newQueue = [...queue, track]
        // If nothing is playing, start this track
        if (!currentTrack) {
          set({
            queue: newQueue,
            queueIndex: newQueue.length - 1,
            currentTrack: track,
            isPlaying: true,
            isVisible: true,
            currentTime: 0,
          })
        } else {
          set({ queue: newQueue })
        }
      },

      removeFromQueue: (index) => {
        const { queue, queueIndex } = get()
        const newQueue = queue.filter((_, i) => i !== index)
        set({
          queue: newQueue,
          queueIndex: index < queueIndex ? queueIndex - 1 : queueIndex,
        })
      },

      clearQueue: () => set({ queue: [], queueIndex: -1 }),

      playFromQueue: (index) => {
        const { queue } = get()
        if (index < 0 || index >= queue.length) return
        set({
          currentTrack: queue[index],
          queueIndex: index,
          isPlaying: true,
          currentTime: 0,
        })
      },

      setAudioLedMode: (enabled) => set({ isAudioLedMode: enabled }),
      setAudioLedLoop: (start, end) => set({ audioLedLoopStart: start, audioLedLoopEnd: end }),
    }),
    {
      name: "sib-storage",
      partialize: (state) => ({
        volume: state.volume,
        isMuted: state.isMuted,
        repeatMode: state.repeatMode,
        isMinimized: state.isMinimized,
        queue: state.queue,
        queueIndex: state.queueIndex,
      }),
    },
  ),
)
